import { Button } from "@msuite/ui/button";
import { Draggable } from "@msuite/ui/dnd";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { ConfirmModal, ModalWrapper, useAsyncModal } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import { moment } from "@msuite/utils/date";
import { guardString } from "@msuite/utils/strings";
import React from "react";
import { client } from "@/hono";
import type { CrewDefinitionListItem } from "@/hooks/use-crew-definitions";
import { EditCrewDefinitionModal } from "@/modals/plan/edit-crew-definition-modal";
import { CrewDefinitionTags } from "./crew-definition-tags";

/** Props Interface */
interface CrewDefinitionCardProps {
	crewDefinition: CrewDefinitionListItem;
}

export function CrewDefinitionCard({ crewDefinition }: CrewDefinitionCardProps) {
	/** Hooks */
	const { t } = useTranslation();
	const qc = useQueryClient();
	const editModal = useDisclosure();
	const asyncModal = useAsyncModal();

	/** Functions */
	async function handleOnDelete() {
		const isConfirmed = await asyncModal.confirm();
		if (!isConfirmed) return;
		const response = await client.crew_definitions[":crewDefinitionId"].$delete({
			param: { crewDefinitionId: crewDefinition.id },
		});
		await responseHandler(response);
		await qc.invalidateQueries({ predicate: invalidate(["crew-definitions"]) });
	}

	/** Computed */
	// An open-ended crew is the common case, so the span reads as a start with an em dash rather
	// than inventing an end the row does not have.
	const span = `${moment(crewDefinition.startDate).format("DD.MM.YYYY")} – ${
		crewDefinition.endDate ? moment(crewDefinition.endDate).format("DD.MM.YYYY") : "∞"
	}`;

	/** Render */
	return (
		<React.Fragment>
			<Draggable
				type="crew-definition"
				item={{ crewDefinition, managerSlot: null }}
				className="v-stack gap-2 card p-2"
			>
				<div className="h-stack justify-between gap-2">
					<div className="v-stack gap-0 min-w-0">
						<p className="text-sm truncate">
							{guardString(crewDefinition.displayName, t("planning.crews.unnamed_crew"))}
						</p>
						<p className="label truncate">{span}</p>
					</div>
					<div className="h-stack gap-1 shrink-0">
						<Button
							variant="white"
							size="xxs"
							icon={<Icon name="Pencil" />}
							onClick={editModal.onOpen}
						/>
						<Button
							variant="white"
							size="xxs"
							icon={<Icon name="Trash2" />}
							onClick={handleOnDelete}
						/>
					</div>
				</div>
				<CrewDefinitionTags crewDefinition={crewDefinition} />
			</Draggable>
			{editModal.isOpen && (
				<EditCrewDefinitionModal
					isOpen={editModal.isOpen}
					onClose={editModal.onClose}
					crewDefinitionId={crewDefinition.id}
				/>
			)}
			<ModalWrapper>
				{asyncModal.isOpen && (
					<ConfirmModal
						controller={asyncModal}
						message={t("confirmation.confirm_deletion")}
					/>
				)}
			</ModalWrapper>
		</React.Fragment>
	);
}
